import React, { FormEvent } from 'react'
import { useForm, Link } from '@inertiajs/react'
import * as yup from 'yup'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormHelperText from '@mui/material/FormHelperText'
import { AppLayout } from '@/components/layouts/AppLayout'

interface ProjectRef {
  id: number
  name: string
}

interface Props {
  projects: ProjectRef[]
}

const notebookSchema = yup.object({
  notebook: yup.object({
    name: yup.string().required('Notebook name is required').max(100, 'Notebook name must be at most 100 characters'),
    project_id: yup.number().required('Project is required').min(1, 'Project is required'),
  })
})

export default function New({ projects }: Props) {
  const { data, setData, post, processing, errors, setError, clearErrors } = useForm({
    notebook: {
      name: '',
      description: '',
      project_id: projects.length === 1 ? projects[0].id : '' as any,
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    try {
      notebookSchema.validateSync(data, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    post('/notebooks/notebooks')
  }

  return (
    <AppLayout title="New notebook">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <FormControl fullWidth sx={{ mb: 3 }} error={'notebook.project_id' in errors}>
            <InputLabel>Project</InputLabel>
            <Select
              value={data.notebook.project_id}
              label="Project"
              onChange={e => setData('notebook', { ...data.notebook, project_id: e.target.value as number })}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
            {('notebook.project_id' in errors) && (
              <FormHelperText>{(errors as Record<string, string>)['notebook.project_id']}</FormHelperText>
            )}
          </FormControl>
          <TextField
            label="Notebook name"
            fullWidth
            autoFocus
            value={data.notebook.name}
            onChange={e => setData('notebook', { ...data.notebook, name: e.target.value })}
            error={'notebook.name' in errors}
            helperText={(errors as Record<string, string>)['notebook.name']}
            sx={{ mb: 3 }}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            value={data.notebook.description}
            onChange={e => setData('notebook', { ...data.notebook, description: e.target.value })}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Create notebook
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/notebooks/notebooks" style={{ color: '#1a1a1a' }}>Back to notebooks</Link>
          </Box>
        </Box>
      </Paper>
    </AppLayout>
  )
}
